import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { InstancesProvider } from './contexts/InstancesContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedByLevel from './components/ProtectedByLevel';
import GlobalConfirmModal from './components/GlobalConfirmModal';
import ErrorBoundary from './components/ErrorBoundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/query';
// Import do arquivo específico, não do barrel: pelo barrel, o pacote principal levava
// DataTable, Dialog e a paleta inteira mesmo em quem só precisa do Spinner.
import { Spinner } from './ui/primitives';
import { TooltipProvider } from './ui/overlays';

// A paleta só existe quando alguém aperta ⌘K — não precisa pesar na primeira carga.
const PaletaComandos = lazy(() => import('./components/PaletaComandos'));

// Rotas carregadas sob demanda. Antes tudo era import estático, então abrir o LOGIN baixava
// reactflow, recharts, leaflet, emoji-picker e o editor de fluxo de 2 mil linhas.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ContaSuspensa = lazy(() => import('./pages/ContaSuspensa'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const FilaHoje = lazy(() => import('./pages/FilaHoje'));
const Crm = lazy(() => import('./pages/Crm'));
const AppConfig = lazy(() => import('./pages/AppConfig'));
const InstancesManagement = lazy(() => import('./pages/InstancesManagement'));
const Canais = lazy(() => import('./pages/Canais'));
const Chips = lazy(() => import('./pages/Chips'));
const Operacao = lazy(() => import('./pages/Operacao'));
const ChipDetalhe = lazy(() => import('./pages/ChipDetalhe'));
const Captacao = lazy(() => import('./pages/Captacao'));
const Retomar = lazy(() => import('./pages/Retomar'));
const Experimentos = lazy(() => import('./pages/Experimentos'));
const Playbook = lazy(() => import('./pages/Playbook'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupDetails = lazy(() => import('./pages/GroupDetails'));
const WhatsappGroups = lazy(() => import('./pages/WhatsappGroups'));
const WhatsappGroupDetail = lazy(() => import('./pages/WhatsappGroupDetail'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const CampaignDetails = lazy(() => import('./pages/CampaignDetails'));
const CampaignForm = lazy(() => import('./pages/CampaignForm'));
const Templates = lazy(() => import('./pages/Templates'));
const Messages = lazy(() => import('./pages/Messages'));
const Users = lazy(() => import('./pages/Users'));
const Sessions = lazy(() => import('./pages/Sessions'));
const Atendimento = lazy(() => import('./pages/Atendimento'));
const Analytics = lazy(() => import('./pages/Analytics'));
const InstagramPosts = lazy(() => import('./pages/InstagramPosts'));
const HashtagMonitoring = lazy(() => import('./pages/HashtagMonitoring'));
const CompetitiveIntelligence = lazy(() => import('./pages/CompetitiveIntelligence'));
const IdeaGenerator = lazy(() => import('./pages/IdeaGenerator'));
const RoteiroEditor = lazy(() => import('./pages/IdeaGenerator/RoteiroEditor'));
const NotFound = lazy(() => import('./pages/NotFound'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const Agentes = lazy(() => import('./pages/Agentes'));
const AgenteDetalhe = lazy(() => import('./pages/Agentes').then((m) => ({ default: m.AgenteDetalhe })));
const Funil = lazy(() => import('./pages/Funil'));
const Prospeccao = lazy(() => import('./pages/Prospeccao'));
const Configuracao = lazy(() => import('./pages/Configuracao'));
const Abordagens = lazy(() => import('./pages/Abordagens'));
const Aquecimento = lazy(() => import('./pages/Aquecimento'));
const Metas = lazy(() => import('./pages/Metas'));

function Carregando() {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <Spinner className="h-6 w-6 text-brand" />
    </div>
  );
}

/** Rota protegida: exige sessão e contém a falha na própria tela. */
const P = ({ children }) => (
  <ProtectedRoute>
    <ErrorBoundary>{children}</ErrorBoundary>
  </ProtectedRoute>
);

/** Rota pública (login etc.) — também precisa de fronteira de erro. */
const Pub = ({ children }) => <ErrorBoundary>{children}</ErrorBoundary>;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <InstancesProvider>
          <TooltipProvider>
          <Router>
            <div className="App">
              <GlobalConfirmModal />
              {/* Paleta ⌘K: dentro do Router (navega) e do ThemeProvider (troca o tema). É lazy e fica FORA
                  das rotas: sem boundary próprio, o chunk dela sumir depois de um deploy desmontava o app
                  inteiro (tela branca, sem recarga). Provado com o chunk apagado em 30/09 (B1 G4 I4). */}
              <ErrorBoundary silencioso>
                <Suspense fallback={null}>
                  <PaletaComandos />
                </Suspense>
              </ErrorBoundary>
              <Suspense fallback={<Carregando />}>
                <Routes>
                  {/* Auth */}
                  <Route path="/login" element={<Pub><Login /></Pub>} />
                  <Route path="/register" element={<Pub><Register /></Pub>} />
                  <Route path="/forgot-password" element={<Pub><ForgotPassword /></Pub>} />
                  <Route path="/reset-password" element={<Pub><ResetPassword /></Pub>} />
                  <Route path="/conta-suspensa" element={<Pub><ContaSuspensa /></Pub>} />

                  {/* Cockpit */}
                  <Route path="/" element={<P><Dashboard /></P>} />
                  <Route path="/hoje" element={<P><FilaHoje /></P>} />

                  {/* Prospecção */}
                  <Route path="/captacao" element={<P><Captacao /></P>} />
                  <Route path="/operacao" element={<P><Operacao /></P>} />
                  <Route path="/chips" element={<P><Chips /></P>} />
                  <Route path="/chips/:id" element={<P><ChipDetalhe /></P>} />
                  <Route path="/configuracoes/canais" element={<P><Canais /></P>} />
                  <Route path="/instances" element={<P><InstancesManagement /></P>} />
                  <Route path="/marketing/campanhas" element={<P><Campaigns /></P>} />
                  <Route path="/marketing/campanhas/nova" element={<P><CampaignForm /></P>} />
                  <Route path="/marketing/campanhas/editar/:id" element={<P><CampaignForm /></P>} />
                  <Route path="/marketing/campanhas/:id" element={<P><CampaignDetails /></P>} />
                  <Route path="/marketing/templates" element={<P><Templates /></P>} />
                  <Route path="/experimentos" element={<P><Experimentos /></P>} />
                  <Route path="/agentes" element={<P><Agentes /></P>} />
                  <Route path="/abordagens" element={<P><Abordagens /></P>} />
                  <Route path="/aquecimento" element={<P><Aquecimento /></P>} />
                  <Route path="/agentes/:id" element={<P><AgenteDetalhe /></P>} />
                  <Route path="/funil" element={<P><Funil /></P>} />
                  <Route path="/metas" element={<P><Metas /></P>} />
                  <Route path="/prospeccao" element={<P><Prospeccao /></P>} />

                  {/* Atendimento */}
                  <Route path="/conversas" element={<P><Atendimento /></P>} />
                  <Route path="/retomar" element={<P><Retomar /></P>} />
                  <Route path="/playbook" element={<P><Playbook /></P>} />
                  <Route path="/config" element={<P><Configuracao /></P>} />
                  <Route path="/config/assistente" element={<P><AppConfig /></P>} />
                  <Route path="/contatos" element={<P><Crm /></P>} />
                  <Route path="/contatos/agenda" element={<P><Contacts /></P>} />
                  <Route path="/segmentos" element={<P><Groups /></P>} />
                  <Route path="/segmentos/:id" element={<P><GroupDetails /></P>} />
                  <Route path="/grupos-wa" element={<P><WhatsappGroups /></P>} />
                  <Route path="/grupos-wa/:id" element={<P><WhatsappGroupDetail /></P>} />

                  {/* Insights / Marketing */}
                  <Route path="/insights/publicacoes" element={<P><InstagramPosts /></P>} />
                  <Route path="/insights/hashtags" element={<P><HashtagMonitoring /></P>} />
                  <Route path="/insights/inteligencia" element={<P><CompetitiveIntelligence /></P>} />
                  <Route path="/insights/ideias" element={<P><IdeaGenerator /></P>} />
                  <Route path="/insights/ideias/roteiro/:id" element={<P><RoteiroEditor /></P>} />

                  {/* Análise */}
                  <Route path="/analytics" element={<P><Analytics /></P>} />

                  {/* Vitrine do kit — onde se revisa o design system sem abrir o produto */}
                  <Route path="/design-system" element={<P><DesignSystem /></P>} />

                  {/* Config */}
                  <Route
                    path="/configuracoes/usuarios"
                    element={
                      <P>
                        <ProtectedByLevel niveisPermitidos={['admin', 'gerente']}>
                          <Users />
                        </ProtectedByLevel>
                      </P>
                    }
                  />

                  {/* Legado / atalhos */}
                  <Route path="/messages" element={<P><Messages /></P>} />
                  <Route path="/sessions" element={<P><Sessions /></P>} />

                  {/* Endereços antigos: um destino canônico por tela, para o menu não marcar
                      "ativo" em dois itens e para o histórico do navegador não duplicar. */}
                  {/* "Carla × Bruno" foi dissolvida na Fila de Hoje (ADR-2 da missão
                      revisao-profunda-abas): o experimento não é um LUGAR, é como se aborda. */}
                  <Route path="/experimento-campo" element={<Navigate to="/hoje" replace />} />
                  <Route path="/atendimento" element={<Navigate to="/conversas" replace />} />
                  <Route path="/atendimento/agente-ia" element={<Navigate to="/config" replace />} />
                  <Route path="/configuracoes/automacao" element={<Navigate to="/config" replace />} />
                  <Route path="/marketing/publicacoes" element={<Navigate to="/insights/publicacoes" replace />} />
                  <Route path="/analytics/mensagens" element={<Navigate to="/analytics" replace />} />
                  <Route path="/analytics/redes-sociais" element={<Navigate to="/analytics" replace />} />
                  <Route path="/insights/analytics" element={<Navigate to="/analytics" replace />} />

                  <Route path="*" element={<Pub><NotFound /></Pub>} />
                </Routes>
              </Suspense>
              {/* Um único sistema de aviso. O ToastProvider próprio foi removido: eram dois
                  contêineres na mesma posição da tela, e o custom ignorava os tokens de tema. */}
              <ToastContainer position="top-right" theme="colored" />
            </div>
          </Router>
          </TooltipProvider>
        </InstancesProvider>
      </AuthProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
